/**
 * ISO image writer: ISO 9660 + Joliet + UDF 1.02 bridge (+ El Torito, + isohybrid MBR).
 *
 * Surface: window.__DiskImage.IsoBuilder
 *   var b = new IsoBuilder({ volumeId, publisher, joliet: true, udf: true, hybridMbr: false });
 *   b.addFile('/path/in/iso/file.bin', fileOrBlob)
 *   b.addDir('/path/dir')
 *   b.setBootImage(file, { platform: 'BIOS'|'EFI', emulation: 'no-emul'|'1.44'|'hd' })
 *   var out = await b.build()
 *     → { blob, size, totalSectors, layout, renamed: [{ path, joliet, udf }], fs: [...] }
 *
 * WHICH READER WALKS WHAT — measured 2026-09-25 by mounting the images in
 * Windows (Mount-DiskImage = cdfs.sys / udfs.sys, the reader a Windows user
 * has) and opening them in 7-Zip, not by reading them back with our own parser.
 * Our parser and 7-Zip walk the directory RECORDS; Windows navigates Joliet by
 * the PATH TABLE. Every defect below was invisible to the first two and fatal on
 * the third, which is how a bug report «half the files in the ISO are missing»
 * reached us before any test did:
 *
 *   - The path tables were built BEFORE the final layout pass moved every
 *     directory once they outgrew one sector (~40–50 folders): Windows read the
 *     wrong sectors as folders — a 161-folder tree showed 2 files of 882, a
 *     300-folder one showed other folders' files under their names. There is
 *     now ONE layout pass and one allocator (_plan); everything is written from
 *     its numbers, nothing re-derives them.
 *   - A file over 4 GiB had its size written into a 32-bit field: a 5 GB file
 *     came back as 1 GB in both readers. ISO 9660 now splits it into file
 *     sections (multi-extent records, flag 0x80), UDF carries it natively, and
 *     every 32-bit field refuses to truncate (both32 throws).
 *   - A Joliet name longer than ~110 characters overflowed the one-byte record
 *     length — the whole image became unreadable to both readers. Joliet names
 *     are now capped (JOLIET_MAX, measured) with the extension kept; the full
 *     name lives in UDF.
 *   - '?' '*' ':' in a name made Windows list NOTHING in that directory; ';'
 *     cut the name at the ';' (Joliet reads it as the version separator); two
 *     names differing only in case hid one of them. Names are cleaned and made
 *     unique case-insensitively, per directory, per namespace.
 *
 * Why UDF: it is what Windows reads first when present (and what ImgBurn,
 * AnyBurn and oscdimg write by default) — full names up to 127/254 characters,
 * files of any size, the real characters. ISO 9660 + Joliet stay for every
 * reader that does not speak UDF (car stereos, consoles, old systems).
 *
 * Memory: file contents go into the Blob BY REFERENCE (zero-copy); metadata is
 * generated in LBA order and flushed to Blob chunks as it goes, so a
 * 50 000-file image never holds its metadata on the JS heap at once.
 */
(function (w) {
    'use strict';
    w.__DiskImage = w.__DiskImage || {};
    if (w.__DiskImage.IsoBuilder) return;

    var SECTOR = 2048;
    // One ISO 9660 file section: every section but the last must be a whole
    // number of blocks, and the length field is 32-bit → 4 GiB − 1 block.
    var ISO_MAX_EXTENT = 0xFFFFF800;
    // One UDF short_ad: the top two bits of the 32-bit length are the extent
    // type, so 1 GiB − 1 block.
    var UDF_MAX_EXTENT = 0x3FFFF800;
    // How many short_ads fit in one File Entry block (176-byte header).
    var UDF_MAX_ADS = Math.floor((SECTOR - 176) / 8);
    // Joliet names, in UCS-2 units. The spec says 64; mkisofs -joliet-long
    // writes 103, and that is what every reader we measured takes. The record
    // length is ONE byte (33 + 2×103 + 1 = 240 ≤ 255).
    var JOLIET_MAX = 103;
    var UDF_ANCHOR = 256;

    // ── bytes ──────────────────────────────────────────────────────────────
    function le16(b, o, v) { b[o] = v & 255; b[o + 1] = (v >>> 8) & 255; }
    function be16(b, o, v) { b[o] = (v >>> 8) & 255; b[o + 1] = v & 255; }
    function both16(b, o, v) { le16(b, o, v); be16(b, o + 2, v); }
    function le32(b, o, v) { b[o] = v & 255; b[o + 1] = (v >>> 8) & 255; b[o + 2] = (v >>> 16) & 255; b[o + 3] = (v >>> 24) & 255; }
    function be32(b, o, v) { b[o] = (v >>> 24) & 255; b[o + 1] = (v >>> 16) & 255; b[o + 2] = (v >>> 8) & 255; b[o + 3] = v & 255; }
    // A 32-bit field never truncates silently: that silence is how a 5 GB file
    // became a 1 GB one with no error anywhere.
    function both32(b, o, v) {
        if (!(v >= 0 && v <= 0xFFFFFFFF)) throw new Error('iso-writer: value ' + v + ' does not fit a 32-bit field');
        le32(b, o, v); be32(b, o + 4, v);
    }
    function le64(b, o, v) { le32(b, o, v >>> 0); le32(b, o + 4, Math.floor(v / 4294967296)); }
    function ascii(b, o, s, len, fill) {
        s = String(s || '');
        for (var i = 0; i < len; i++) b[o + i] = i < s.length ? (s.charCodeAt(i) & 0x7F) : (fill == null ? 0x20 : fill);
    }
    // Joliet text fields: UCS-2 big-endian, padded with UCS-2 spaces.
    function ucs2(b, o, s, byteLen) {
        s = String(s || '');
        for (var p = 0, i = 0; p < byteLen; p += 2, i++) {
            var c = i < s.length ? s.charCodeAt(i) : 0x20;
            b[o + p] = (c >>> 8) & 255; b[o + p + 1] = c & 255;
        }
    }
    function sectorsFor(bytes) { return Math.ceil(bytes / SECTOR); }

    // ── dates ──────────────────────────────────────────────────────────────
    function recDate(b, o, d) {               // ECMA-119 9.1.5, 7 bytes, UTC
        b[o] = d.getUTCFullYear() - 1900; b[o + 1] = d.getUTCMonth() + 1; b[o + 2] = d.getUTCDate();
        b[o + 3] = d.getUTCHours(); b[o + 4] = d.getUTCMinutes(); b[o + 5] = d.getUTCSeconds(); b[o + 6] = 0;
    }
    function decDate(b, o, d) {               // ECMA-119 8.4.26.1, 17 bytes; null = "not specified"
        if (!d) { for (var z = 0; z < 16; z++) b[o + z] = 0x30; b[o + 16] = 0; return; }
        var p = function (n, k) { var s = '' + n; while (s.length < k) s = '0' + s; return s; };
        ascii(b, o, p(d.getUTCFullYear(), 4) + p(d.getUTCMonth() + 1, 2) + p(d.getUTCDate(), 2) +
            p(d.getUTCHours(), 2) + p(d.getUTCMinutes(), 2) + p(d.getUTCSeconds(), 2) + '00', 16);
        b[o + 16] = 0;
    }
    function udfTime(b, o, d) {               // ECMA-167 1/7.3, type 1, UTC
        le16(b, o, 0x1000);
        le16(b, o + 2, d.getUTCFullYear());
        b[o + 4] = d.getUTCMonth() + 1; b[o + 5] = d.getUTCDate();
        b[o + 6] = d.getUTCHours(); b[o + 7] = d.getUTCMinutes(); b[o + 8] = d.getUTCSeconds();
        b[o + 9] = 0; b[o + 10] = 0; b[o + 11] = 0;
    }

    // ── names ──────────────────────────────────────────────────────────────
    // What Windows refuses in a name, plus control characters. Measured: one '?'
    // in a Joliet name and Windows listed nothing at all in that directory.
    var WIN_BAD = /[\u0000-\u001F"*\/:<>?\\|]/g;
    function cleanLong(name) {
        var s = String(name).replace(WIN_BAD, '_');
        // Windows drops trailing dots and spaces when it opens a name, and the
        // shortened name then matches no record.
        s = s.replace(/[. ]+$/, '');
        return s || '_';
    }
    // Joliet also reserves ';' — Windows reads "a;b.txt" as "a" (version separator).
    function jolietName(name) { return cleanLong(name).replace(/;/g, '_'); }
    function udfName(name) { return cleanLong(name); }

    // Cut to n UTF-16 units without splitting a surrogate pair.
    function cutUnits(s, n) {
        if (s.length <= n) return s;
        var c = s.charCodeAt(n - 1);
        return (c >= 0xD800 && c <= 0xDBFF) ? s.slice(0, n - 1) : s.slice(0, n);
    }
    // Fit `name` + `suffix` into `max` units, keeping the extension: it is what
    // tells the reader what the file is.
    function fitName(name, isDir, max, suffix) {
        var base = name, ext = '';
        if (!isDir) {
            var dot = name.lastIndexOf('.');
            if (dot > 0 && name.length - dot <= 17) { base = name.slice(0, dot); ext = name.slice(dot); }
        }
        if (base.length + suffix.length + ext.length <= max) return base + suffix + ext;
        var room = max - suffix.length - ext.length;
        if (room < 1) return cutUnits(name, max - suffix.length) + suffix;
        return cutUnits(base, room) + suffix + ext;
    }
    // Unique within one directory, case-insensitively: Windows is, and of two
    // names differing only in case it showed one.
    function claimLong(taken, name, isDir, max) {
        for (var n = 1; n < 1000000; n++) {
            var cand = fitName(name, isDir, max, n === 1 ? '' : ' (' + n + ')');
            var key = cand.toUpperCase();
            if (!taken.has(key)) { taken.add(key); return cand; }
        }
        throw new Error('iso-writer: cannot make a unique name for ' + name);
    }

    // ISO 9660 level 2 identifiers: d-characters, file name + extension ≤ 30,
    // the '.' is mandatory even with no extension (ECMA-119 7.5.1), directory
    // identifiers ≤ 31. The separator is chosen BEFORE cleaning: the class does
    // not contain '.', and cleaning first turned README.TXT into README_TXT.
    function isoClean(s) { return String(s).toUpperCase().replace(/[^A-Z0-9_]/g, '_').replace(/^_+|_+$/g, ''); }
    function isoIdent(name, isDir, n) {
        // '~N' (the old suffix) is not a d-character; '_N' is.
        var sfx = n > 1 ? '_' + (n - 1) : '';
        if (isDir) return (isoClean(name) || 'DIR').slice(0, 31 - sfx.length) + sfx;
        var raw = String(name), dot = raw.lastIndexOf('.');
        var base = isoClean(dot >= 0 ? raw.slice(0, dot) : raw) || 'FILE';
        var ext = dot >= 0 ? isoClean(raw.slice(dot + 1)).slice(0, 8) : '';
        return base.slice(0, 30 - ext.length - sfx.length) + sfx + '.' + ext + ';1';
    }
    function claimIso(taken, name, isDir) {
        for (var n = 1; n < 1000000; n++) {
            var id = isoIdent(name, isDir, n);
            if (!taken.has(id)) { taken.add(id); return id; }
        }
        throw new Error('iso-writer: cannot make a unique ISO 9660 identifier for ' + name);
    }
    function isoVolumeId(s) { return (String(s || '').toUpperCase().replace(/[^A-Z0-9_]/g, '_').slice(0, 32)) || 'CDROM'; }

    // ── directory records (ECMA-119 9.1) ──────────────────────────────────
    function recLen(lenFi) {
        var n = 33 + lenFi + (lenFi % 2 === 0 ? 1 : 0);
        if (n > 255) throw new Error('iso-writer: directory record of ' + n + ' bytes (identifier ' + lenFi + ')');
        return n;
    }
    // Place records: none may cross a sector boundary. Returns the sector count;
    // stores each record's offset on it — the writer uses those offsets, so the
    // size and the bytes cannot disagree.
    function placeRecords(recs) {
        var off = 0;
        for (var i = 0; i < recs.length; i++) {
            var r = recs[i];
            r.len = recLen(r.lenFi);
            var end = Math.floor(off / SECTOR) * SECTOR + SECTOR;
            if (off + r.len > end) off = end;
            r.off = off;
            off += r.len;
        }
        return Math.max(1, sectorsFor(off));
    }

    // ── UDF primitives (ECMA-167 / OSTA UDF 1.02) ─────────────────────────
    var CRC16 = (function () {
        var t = new Uint16Array(256);
        for (var i = 0; i < 256; i++) {
            var c = i << 8;
            for (var k = 0; k < 8; k++) c = (c & 0x8000) ? ((c << 1) ^ 0x1021) : (c << 1);
            t[i] = c & 0xffff;
        }
        return t;
    })();
    function crc16(b, off, len) {
        var c = 0;
        for (var i = 0; i < len; i++) c = ((c << 8) ^ CRC16[((c >> 8) ^ b[off + i]) & 0xff]) & 0xffff;
        return c;
    }
    // Written LAST: checksum and CRC cover the finished body.
    function tag(b, off, id, location, crcLength) {
        le16(b, off, id); le16(b, off + 2, 2);
        b[off + 4] = 0; b[off + 5] = 0; le16(b, off + 6, 0);
        le16(b, off + 8, crc16(b, off + 16, crcLength));
        le16(b, off + 10, crcLength);
        le32(b, off + 12, location);
        var sum = 0;
        for (var i = 0; i < 16; i++) if (i !== 4) sum += b[off + i];
        b[off + 4] = sum & 255;
    }
    function needs16(s) { return /[^\u0000-ÿ]/.test(s); }
    // OSTA CS0 d-characters: compression id, then 8-bit or UCS-2BE units.
    function csBytes(s) {
        var wide = needs16(s), out = new Uint8Array(1 + s.length * (wide ? 2 : 1));
        out[0] = wide ? 16 : 8;
        for (var i = 0; i < s.length; i++) {
            var c = s.charCodeAt(i);
            if (wide) { out[1 + i * 2] = (c >>> 8) & 255; out[2 + i * 2] = c & 255; } else out[1 + i] = c;
        }
        return out;
    }
    // dstring: fixed field, LAST byte = used length (forgetting it = empty names).
    function dstring(b, off, s, fieldLen) {
        s = String(s || '');
        for (var z = 0; z < fieldLen; z++) b[off + z] = 0;
        if (!s.length) return;
        var room = needs16(s) ? Math.floor((fieldLen - 2) / 2) : fieldLen - 2;
        var cs = csBytes(cutUnits(s, room));
        b.set(cs, off);
        b[off + fieldLen - 1] = cs.length;
    }
    function charspec(b, off) { b[off] = 0; ascii(b, off + 1, 'OSTA Compressed Unicode', 63, 0); }
    function regid(b, off, id, udfSuffix) {
        b[off] = 0;
        ascii(b, off + 1, id, 23, 0);
        for (var i = 0; i < 8; i++) b[off + 24 + i] = 0;
        if (udfSuffix) le16(b, off + 24, 0x0102);
    }
    function longAd(b, off, len, block, uniqueId) {
        le32(b, off, len); le32(b, off + 4, block); le16(b, off + 8, 0);
        le16(b, off + 10, 0); le32(b, off + 12, uniqueId || 0);   // ADImpUse: flags + UDF unique id
    }
    var IMPL = '*Timbrica ISO';

    // ── the builder ────────────────────────────────────────────────────────
    function IsoBuilder(opts) {
        opts = opts || {};
        this.volumeId = String(opts.volumeId || 'VOLUME');
        this.publisher = String(opts.publisher || '');
        this.preparer = opts.preparer || 'TIMBRICA';
        this.applicationId = opts.applicationId || 'TIMBRICA-ISO-CREATOR';
        this.systemId = opts.systemId || 'TIMBRICA';
        this.joliet = opts.joliet !== false;
        this.udf = opts.udf !== false;
        this.hybridMbr = !!opts.hybridMbr;
        this.bootImage = null;
        this.bootOpts = {};
        this.created = opts.created || new Date();
        this.root = { name: '', isDir: true, children: [], parent: null };
    }

    IsoBuilder.prototype.addFile = function (path, file) {
        if (!file || file.size == null) throw new Error('addFile: invalid file');
        var node = this._ensurePath(path, false);
        node.file = file;
        node.size = file.size;
        node.mtime = file.lastModified ? new Date(file.lastModified) : null;
        return node;
    };
    IsoBuilder.prototype.addDir = function (path) { return this._ensurePath(path, true); };
    IsoBuilder.prototype.setBootImage = function (file, opts) { this.bootImage = file; this.bootOpts = opts || {}; };
    IsoBuilder.prototype.setHybridMbr = function (on) { this.hybridMbr = !!on; };

    // A file and a folder may share a name (a/ and a): two nodes, and the
    // uniquifier gives them distinct identifiers — never one silently absorbing
    // the other.
    IsoBuilder.prototype._ensurePath = function (path, isDir) {
        var parts = String(path || '/').replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
        var cur = this.root;
        for (var i = 0; i < parts.length; i++) {
            var nm = parts[i], wantDir = i < parts.length - 1 || isDir, hit = null;
            for (var k = 0; k < cur.children.length; k++) {
                var c = cur.children[k];
                if (c.name === nm && c.isDir === wantDir) { hit = c; break; }
            }
            if (!hit) { hit = { name: nm, isDir: wantDir, children: [], parent: cur }; cur.children.push(hit); }
            cur = hit;
        }
        return cur;
    };

    function byKey(k) { return function (a, b) { return a[k] < b[k] ? -1 : a[k] > b[k] ? 1 : 0; }; }
    function pathOf(n) { var p = []; for (; n && n.parent; n = n.parent) p.unshift(n.name); return '/' + p.join('/'); }

    /**
     * THE layout. Names, record order, extent sizes and every sector address are
     * decided here once, from one allocator, and the emitters only read them.
     */
    IsoBuilder.prototype._plan = function () {
        var self = this, J = this.joliet, U = this.udf, boot = this.bootImage;
        var dirs = [], files = [], renamed = [];

        (function walk(d) {
            d.children = d.children.filter(function (c) { return c.isDir || c.file; });
            dirs.push(d);
            var kids = d.children.slice().sort(byKey('name'));
            kids.forEach(function (c) { if (!c.isDir) files.push(c); });
            kids.forEach(function (c) { if (c.isDir) walk(c); });
        })(this.root);
        if (dirs.length > 65535) throw new Error('iso-writer: ' + dirs.length + ' folders — a path table holds 65535');

        // 1. Identifiers, frozen per directory per namespace.
        dirs.forEach(function (d) {
            var ti = new Set(), tj = new Set(), tu = new Set();
            d.children.slice().sort(byKey('name')).forEach(function (c) {
                c.isoId = claimIso(ti, c.name, c.isDir);
                if (J) c.jolId = claimLong(tj, jolietName(c.name), c.isDir, JOLIET_MAX);
                if (U) { var un = udfName(c.name); c.udfId = claimLong(tu, un, c.isDir, needs16(un) ? 127 : 254); }
                if ((J && c.jolId !== c.name) || (U && c.udfId !== c.name)) {
                    renamed.push({ path: pathOf(c), joliet: J ? c.jolId : null, udf: U ? c.udfId : null });
                }
            });
            d.isoKids = d.children.slice().sort(byKey('isoId'));      // ECMA-119 9.3: identifier order
            if (J) d.jolKids = d.children.slice().sort(byKey('jolId'));
            if (U) d.udfKids = d.children.slice().sort(byKey('udfId'));
        });

        // 2. Directory records and extent sizes.
        function recsOf(d, jol) {
            var out = [{ special: 0, lenFi: 1 }, { special: 1, lenFi: 1 }];
            (jol ? d.jolKids : d.isoKids).forEach(function (c) {
                var lenFi = jol ? c.jolId.length * 2 : c.isoId.length;
                var parts = c.isDir ? 1 : Math.max(1, Math.ceil(c.size / ISO_MAX_EXTENT));
                for (var p = 0; p < parts; p++) out.push({ node: c, lenFi: lenFi, part: p, parts: parts });
            });
            return out;
        }
        dirs.forEach(function (d) {
            d.isoRecs = recsOf(d, false); d.isoSectors = placeRecords(d.isoRecs);
            if (J) { d.jolRecs = recsOf(d, true); d.jolSectors = placeRecords(d.jolRecs); }
        });

        // 3. Path tables (ECMA-119 9.4): breadth-first, children in identifier
        //    order — i.e. sorted by level, then parent number, then name.
        function pathTable(kidsKey, idOf) {
            var order = [self.root], num = new Map();
            num.set(self.root, 1);
            for (var i = 0; i < order.length; i++) {
                order[i][kidsKey].forEach(function (c) { if (c.isDir) { order.push(c); num.set(c, order.length); } });
            }
            var bytes = 0;
            order.forEach(function (d) { var l = d.parent ? idOf(d) : 1; bytes += 8 + l + (l & 1); });
            return { order: order, num: num, bytes: bytes, sectors: Math.max(1, sectorsFor(bytes)) };
        }
        var ptIso = pathTable('isoKids', function (d) { return d.isoId.length; });
        var ptJol = J ? pathTable('jolKids', function (d) { return d.jolId.length * 2; }) : null;

        // 4. UDF directory streams: File Identifier Descriptors, back to back
        //    (ECMA-167 4/14.4; a FID may cross a block boundary — a zero gap
        //    would read as a descriptor with tag 0 and end the listing).
        function fidLen(nameBytes) { return (38 + nameBytes + 3) & ~3; }
        if (U) {
            dirs.forEach(function (d) {
                var bytes = fidLen(0);
                d.udfKids.forEach(function (c) { c.udfCs = csBytes(c.udfId); bytes += fidLen(c.udfCs.length); });
                d.fidBytes = bytes;
            });
            files.forEach(function (f) {
                if (Math.ceil(f.size / UDF_MAX_EXTENT) > UDF_MAX_ADS) {
                    throw new Error('iso-writer: ' + pathOf(f) + ' is larger than one UDF File Entry can describe');
                }
            });
        }

        // 5. Sector allocation — the only place an address is decided.
        var L = {}, cur = 16;
        L.pvd = cur++;
        if (boot) L.brvd = cur++;
        if (J) L.svd = cur++;
        L.term = cur++;
        if (U) { L.vrs = cur; cur += 3; }                 // BEA01 NSR02 TEA01 right after the ISO set
        if (boot) L.catalog = cur++;
        if (U) {
            if (cur > 32) throw new Error('iso-writer: descriptor area overflow');
            L.mvds = 32; L.rvds = 48; L.lvid = 64; cur = 66;
        }
        function alloc(n) {
            // A contiguous run that never covers the UDF anchor at 256.
            if (U && cur <= UDF_ANCHOR && cur + n > UDF_ANCHOR) cur = UDF_ANCHOR + 1;
            var at = cur; cur += n; return at;
        }
        ptIso.lLba = alloc(ptIso.sectors); ptIso.mLba = alloc(ptIso.sectors);
        if (J) { ptJol.lLba = alloc(ptJol.sectors); ptJol.mLba = alloc(ptJol.sectors); }
        dirs.forEach(function (d) {
            d.isoLba = alloc(d.isoSectors);
            if (J) d.jolLba = alloc(d.jolSectors);
        });
        var uid = 16;                                    // UDF: 0 = root, 1..15 reserved
        if (U) {
            if (cur <= UDF_ANCHOR) cur = UDF_ANCHOR + 1;   // partition starts after the anchor
            L.partStart = cur;
            L.fsd = alloc(1); L.fsdTerm = alloc(1);
            dirs.forEach(function (d) {
                d.uid = d.parent ? uid++ : 0;
                d.feLba = alloc(1);
                d.fidLba = alloc(Math.max(1, sectorsFor(d.fidBytes)));
            });
            files.forEach(function (f) { f.uid = uid++; f.feLba = alloc(1); });
        }
        if (boot) L.bootLba = alloc(Math.max(1, sectorsFor(boot.size)));
        // An empty file still gets a sector of its own: readers that pair the
        // Joliet tree with the ISO 9660 one by extent (ours does) would give
        // every empty file sharing one location the same name.
        files.forEach(function (f) { f.lba = alloc(Math.max(1, sectorsFor(f.size))); });
        if (U) { L.anchor2 = alloc(1); L.partLen = L.anchor2 - L.partStart; }
        L.total = cur;

        return { dirs: dirs, files: files, ptIso: ptIso, ptJol: ptJol, L: L, renamed: renamed, nextUid: uid };
    };

    // ── ISO 9660 / Joliet emitters ────────────────────────────────────────
    IsoBuilder.prototype._volumeDescriptor = function (P, type) {
        var b = new Uint8Array(SECTOR), L = P.L, jol = type === 2;
        var pt = jol ? P.ptJol : P.ptIso, root = this.root;
        b[0] = type; ascii(b, 1, 'CD001', 5); b[6] = 1;
        if (jol) {
            ucs2(b, 8, this.systemId, 32); ucs2(b, 40, cutUnits(cleanLong(this.volumeId), 16), 32);
            b[88] = 0x25; b[89] = 0x2F; b[90] = 0x45;   // %/E — UCS-2 level 3
        } else {
            ascii(b, 8, String(this.systemId).toUpperCase(), 32); ascii(b, 40, isoVolumeId(this.volumeId), 32);
        }
        both32(b, 80, L.total);
        both16(b, 120, 1); both16(b, 124, 1); both16(b, 128, SECTOR);
        both32(b, 132, pt.bytes);
        le32(b, 140, pt.lLba); le32(b, 144, 0); be32(b, 148, pt.mLba); be32(b, 152, 0);
        var lba = jol ? root.jolLba : root.isoLba, size = (jol ? root.jolSectors : root.isoSectors) * SECTOR;
        b[156] = 34; both32(b, 158, lba); both32(b, 166, size); recDate(b, 174, this.created);
        b[181] = 0x02; both16(b, 184, 1); b[188] = 1; b[189] = 0;
        var text = jol ? function (o, s, n) { ucs2(b, o, s, n); } : function (o, s, n) { ascii(b, o, String(s).toUpperCase(), n); };
        text(190, jol ? cleanLong(this.volumeId) : isoVolumeId(this.volumeId), 128);
        text(318, this.publisher, 128);
        text(446, this.preparer, 128);
        text(574, this.applicationId, 128);
        text(702, '', 37); text(739, '', 37); text(776, '', 37);
        decDate(b, 813, this.created); decDate(b, 830, this.created); decDate(b, 847, null); decDate(b, 864, null);
        b[881] = 1;
        return b;
    };

    IsoBuilder.prototype._pathTable = function (pt, jol, bigEndian) {
        var b = new Uint8Array(pt.sectors * SECTOR), o = 0;
        pt.order.forEach(function (d) {
            var id = d.parent ? (jol ? d.jolId : d.isoId) : null;
            var lenDi = id == null ? 1 : (jol ? id.length * 2 : id.length);
            b[o] = lenDi; b[o + 1] = 0;
            var lba = jol ? d.jolLba : d.isoLba, parent = d.parent ? pt.num.get(d.parent) : 1;
            if (bigEndian) { be32(b, o + 2, lba); be16(b, o + 6, parent); } else { le32(b, o + 2, lba); le16(b, o + 6, parent); }
            if (id != null) {
                for (var i = 0; i < id.length; i++) {
                    var c = id.charCodeAt(i);
                    if (jol) { b[o + 8 + i * 2] = (c >>> 8) & 255; b[o + 9 + i * 2] = c & 255; } else b[o + 8 + i] = c;
                }
            }
            o += 8 + lenDi + (lenDi & 1);
        });
        return b;
    };

    IsoBuilder.prototype._dirExtent = function (d, jol) {
        var created = this.created;
        var recs = jol ? d.jolRecs : d.isoRecs, b = new Uint8Array((jol ? d.jolSectors : d.isoSectors) * SECTOR);
        function where(n) { return jol ? { lba: n.jolLba, size: n.jolSectors * SECTOR } : { lba: n.isoLba, size: n.isoSectors * SECTOR }; }
        recs.forEach(function (r) {
            var o = r.off, lba, size, flags = 0, date = created, id = null;
            if (r.special === 0) { var me = where(d); lba = me.lba; size = me.size; flags = 2; }
            else if (r.special === 1) { var pa = where(d.parent || d); lba = pa.lba; size = pa.size; flags = 2; }
            else if (r.node.isDir) { var ch = where(r.node); lba = ch.lba; size = ch.size; flags = 2; id = jol ? r.node.jolId : r.node.isoId; }
            else {
                var f = r.node, last = r.part === r.parts - 1;
                lba = f.lba + r.part * (ISO_MAX_EXTENT / SECTOR);
                size = last ? f.size - r.part * ISO_MAX_EXTENT : ISO_MAX_EXTENT;
                flags = last ? 0 : 0x80;                       // multi-extent: more sections follow
                date = f.mtime || created;
                id = jol ? f.jolId : f.isoId;
            }
            b[o] = r.len; b[o + 1] = 0;
            both32(b, o + 2, lba); both32(b, o + 10, size);
            recDate(b, o + 18, date);
            b[o + 25] = flags; b[o + 26] = 0; b[o + 27] = 0;
            both16(b, o + 28, 1);
            b[o + 32] = r.lenFi;
            if (id == null) b[o + 33] = r.special === 1 ? 1 : 0;
            else {
                for (var i = 0; i < id.length; i++) {
                    var c = id.charCodeAt(i);
                    if (jol) { b[o + 33 + i * 2] = (c >>> 8) & 255; b[o + 34 + i * 2] = c & 255; } else b[o + 33 + i] = c;
                }
            }
        });
        return b;
    };

    // ── UDF emitters ───────────────────────────────────────────────────────
    IsoBuilder.prototype._udfVolumeDescriptors = function (P, base) {
        var L = P.L, now = this.created, lv = cleanLong(this.volumeId);
        var out = new Uint8Array(16 * SECTOR);
        function at(i) { return out.subarray(i * SECTOR, (i + 1) * SECTOR); }
        // Primary Volume Descriptor (tag 1)
        var b = at(0);
        le32(b, 16, 0); le32(b, 20, 0);
        dstring(b, 24, lv, 32);
        le16(b, 56, 1); le16(b, 58, 1); le16(b, 60, 2); le16(b, 62, 2);
        le32(b, 64, 1); le32(b, 68, 1);
        dstring(b, 72, lv, 128);
        charspec(b, 200); charspec(b, 264);
        regid(b, 344, IMPL, false); udfTime(b, 376, now); regid(b, 388, IMPL, false);
        tag(b, 0, 1, base, 512 - 16);
        // Implementation Use Volume Descriptor (tag 4) — LV Info
        b = at(1);
        le32(b, 16, 1);
        regid(b, 20, '*UDF LV Info', true);
        charspec(b, 52); dstring(b, 116, lv, 128);
        dstring(b, 244, '', 36); dstring(b, 280, '', 36); dstring(b, 316, '', 36);
        regid(b, 352, IMPL, false);
        tag(b, 0, 4, base + 1, 512 - 16);
        // Partition Descriptor (tag 5)
        b = at(2);
        le32(b, 16, 2); le16(b, 20, 1); le16(b, 22, 0);
        regid(b, 24, '+NSR02', false);
        le32(b, 184, 1);                                  // read-only
        le32(b, 188, L.partStart); le32(b, 192, L.partLen);
        regid(b, 196, IMPL, false);
        tag(b, 0, 5, base + 2, 512 - 16);
        // Logical Volume Descriptor (tag 6)
        b = at(3);
        le32(b, 16, 3); charspec(b, 20); dstring(b, 84, lv, 128);
        le32(b, 212, SECTOR);
        regid(b, 216, '*OSTA UDF Compliant', true);
        longAd(b, 248, SECTOR, L.fsd - L.partStart, 0);  // where the File Set lives
        le32(b, 264, 6); le32(b, 268, 1);
        regid(b, 272, IMPL, false);
        le32(b, 432, 2 * SECTOR); le32(b, 436, L.lvid);  // integrity sequence
        b[440] = 1; b[441] = 6; le16(b, 442, 1); le16(b, 444, 0);   // type-1 partition map
        tag(b, 0, 6, base + 3, 446 - 16);
        // Unallocated Space Descriptor (tag 7)
        b = at(4);
        le32(b, 16, 4); le32(b, 20, 0);
        tag(b, 0, 7, base + 4, 24 - 16);
        // Terminating Descriptor (tag 8)
        b = at(5);
        tag(b, 0, 8, base + 5, 512 - 16);
        return out;
    };

    IsoBuilder.prototype._udfIntegrity = function (P) {
        var L = P.L, out = new Uint8Array(2 * SECTOR), b = out.subarray(0, SECTOR);
        udfTime(b, 16, this.created);
        le32(b, 28, 1);                                   // close integrity
        le64(b, 40, P.nextUid);
        le32(b, 72, 1); le32(b, 76, 46);
        le32(b, 80, 0); le32(b, 84, L.partLen);
        regid(b, 88, IMPL, false);
        le32(b, 120, P.files.length); le32(b, 124, P.dirs.length);
        le16(b, 128, 0x0102); le16(b, 130, 0x0102); le16(b, 132, 0x0102);
        tag(b, 0, 9, L.lvid, 134 - 16);
        tag(out.subarray(SECTOR), 0, 8, L.lvid + 1, 512 - 16);
        return out;
    };

    IsoBuilder.prototype._udfAnchor = function (P, loc) {
        var b = new Uint8Array(SECTOR);
        le32(b, 16, 16 * SECTOR); le32(b, 20, P.L.mvds);
        le32(b, 24, 16 * SECTOR); le32(b, 28, P.L.rvds);
        tag(b, 0, 2, loc, 512 - 16);
        return b;
    };

    IsoBuilder.prototype._udfFileSet = function (P) {
        var L = P.L, out = new Uint8Array(2 * SECTOR), b = out.subarray(0, SECTOR), lv = cleanLong(this.volumeId);
        udfTime(b, 16, this.created);
        le16(b, 28, 3); le16(b, 30, 3); le32(b, 32, 1); le32(b, 36, 1); le32(b, 40, 0); le32(b, 44, 0);
        charspec(b, 48); dstring(b, 112, lv, 128);
        charspec(b, 240); dstring(b, 304, lv, 32);
        dstring(b, 336, '', 32); dstring(b, 368, '', 32);
        longAd(b, 400, SECTOR, this.root.feLba - L.partStart, 0);   // the root ICB
        regid(b, 416, '*OSTA UDF Compliant', true);
        tag(b, 0, 256, L.fsd - L.partStart, 512 - 16);
        tag(out.subarray(SECTOR), 0, 8, L.fsdTerm - L.partStart, 512 - 16);
        return out;
    };

    // File Entry (tag 261). `extents` in partition blocks.
    function fileEntry(loc, isDir, size, extents, links, uniqueId, date) {
        var adLen = extents.length * 8, b = new Uint8Array(SECTOR);
        le32(b, 16, 0); le16(b, 20, 4); le16(b, 22, 0); le16(b, 24, 1);
        b[27] = isDir ? 4 : 5;
        le16(b, 34, 0);                                   // short_ad
        le32(b, 36, 0xFFFFFFFF); le32(b, 40, 0xFFFFFFFF);
        le32(b, 44, isDir ? 0x14A5 : 0x1084);             // r-x / r-- for everyone
        le16(b, 48, links);
        le64(b, 56, size);
        le64(b, 64, sectorsFor(size));
        udfTime(b, 72, date); udfTime(b, 84, date); udfTime(b, 96, date);
        le32(b, 108, 1);
        regid(b, 128, IMPL, false);
        le64(b, 160, uniqueId);
        le32(b, 168, 0); le32(b, 172, adLen);
        for (var i = 0; i < extents.length; i++) {
            le32(b, 176 + i * 8, extents[i].length);      // top 2 bits 0 = recorded & allocated
            le32(b, 180 + i * 8, extents[i].block);
        }
        tag(b, 0, 261, loc, 176 + adLen - 16);
        return b;
    }

    IsoBuilder.prototype._udfDirStream = function (d, P) {
        var ps = P.L.partStart, bytes = new Uint8Array(sectorsFor(d.fidBytes) * SECTOR || SECTOR), off = 0;
        var baseBlock = d.fidLba - ps;
        function fid(cs, isDir, isParent, icbBlock, uniqueId) {
            var nameLen = cs ? cs.length : 0, total = (38 + nameLen + 3) & ~3, o = off;
            le16(bytes, o + 16, 1);
            bytes[o + 18] = (isDir ? 0x02 : 0) | (isParent ? 0x08 : 0);
            bytes[o + 19] = nameLen;
            longAd(bytes, o + 20, SECTOR, icbBlock, uniqueId);
            le16(bytes, o + 36, 0);
            if (cs) bytes.set(cs, o + 38);
            tag(bytes, o, 257, baseBlock + Math.floor(o / SECTOR), total - 16);
            off += total;
        }
        var parent = d.parent || d;
        fid(null, true, true, parent.feLba - ps, parent.uid);
        d.udfKids.forEach(function (c) { fid(c.udfCs, c.isDir, false, c.feLba - ps, c.uid); });
        return bytes;
    };

    // ── El Torito + hybrid MBR ────────────────────────────────────────────
    IsoBuilder.prototype._brvd = function (P) {
        var b = new Uint8Array(SECTOR);
        b[0] = 0; ascii(b, 1, 'CD001', 5); b[6] = 1;
        ascii(b, 7, 'EL TORITO SPECIFICATION', 32, 0);
        le32(b, 71, P.L.catalog);
        return b;
    };
    IsoBuilder.prototype._bootCatalog = function (P) {
        var b = new Uint8Array(SECTOR);
        b[0] = 0x01; b[1] = (this.bootOpts.platform === 'EFI') ? 0xEF : 0x00;
        ascii(b, 4, 'TIMBRICA-ISO-CREATOR', 24, 0);
        b[30] = 0x55; b[31] = 0xAA;
        var sum = 0;
        for (var i = 0; i < 32; i += 2) if (i !== 28) sum = (sum + (b[i] | (b[i + 1] << 8))) & 0xFFFF;
        le16(b, 28, (0x10000 - sum) & 0xFFFF);
        b[32] = 0x88;
        var e = this.bootOpts.emulation;
        b[33] = e === '1.44' ? 2 : e === '1.2' ? 1 : e === '2.88' ? 3 : e === 'hd' ? 4 : 0;
        le16(b, 34, this.bootOpts.loadSegment || 0);
        le16(b, 38, Math.min(0xFFFF, Math.max(1, Math.ceil(this.bootImage.size / 512))));
        le32(b, 40, P.L.bootLba);
        return b;
    };
    // Minimal isohybrid MBR: a partition entry over the whole image + 0x55AA,
    // so `dd` to a USB stick is recognised. Boot code needs isolinux/grub.
    IsoBuilder.prototype._hybridMbr = function (P) {
        var b = new Uint8Array(SECTOR * 16);
        ascii(b, 0, 'TIMBRICA ISOHYBRID  ', 20, 0);
        var n = Math.min(P.L.total * 4, 0xFFFFFFFF);
        b[446] = 0x80; b[447] = 0x00; b[448] = 0x01; b[449] = 0x00;
        b[450] = 0x83; b[451] = 0xFE; b[452] = 0xFF; b[453] = 0xFF;
        le32(b, 454, 0); le32(b, 458, n);
        b[510] = 0x55; b[511] = 0xAA;
        return b;
    };

    // ── assembly ───────────────────────────────────────────────────────────
    IsoBuilder.prototype.build = function () {
        var self = this;
        return new Promise(function (resolve) { resolve(self._assemble(self._plan())); });
    };

    IsoBuilder.prototype._assemble = function (P) {
        var self = this, L = P.L, J = this.joliet, U = this.udf, boot = this.bootImage;
        // Every piece of the image: [lba, sectors, make()] for metadata (built
        // when its turn comes) or a Blob for content.
        var pieces = [];
        function meta(lba, n, make) { pieces.push({ lba: lba, n: n, make: make }); }
        function data(lba, blob) { pieces.push({ lba: lba, n: sectorsFor(blob.size), blob: blob }); }

        meta(0, 16, function () { return self.hybridMbr ? self._hybridMbr(P) : new Uint8Array(16 * SECTOR); });
        meta(L.pvd, 1, function () { return self._volumeDescriptor(P, 1); });
        if (boot) meta(L.brvd, 1, function () { return self._brvd(P); });
        if (J) meta(L.svd, 1, function () { return self._volumeDescriptor(P, 2); });
        meta(L.term, 1, function () { var b = new Uint8Array(SECTOR); b[0] = 0xFF; ascii(b, 1, 'CD001', 5); b[6] = 1; return b; });
        if (U) {
            meta(L.vrs, 3, function () {
                var b = new Uint8Array(3 * SECTOR);
                ['BEA01', 'NSR02', 'TEA01'].forEach(function (id, i) { ascii(b, i * SECTOR + 1, id, 5); b[i * SECTOR + 6] = 1; });
                return b;
            });
        }
        if (boot) meta(L.catalog, 1, function () { return self._bootCatalog(P); });
        if (U) {
            meta(L.mvds, 16, function () { return self._udfVolumeDescriptors(P, L.mvds); });
            meta(L.rvds, 16, function () { return self._udfVolumeDescriptors(P, L.rvds); });
            meta(L.lvid, 2, function () { return self._udfIntegrity(P); });
            meta(UDF_ANCHOR, 1, function () { return self._udfAnchor(P, UDF_ANCHOR); });
            meta(L.anchor2, 1, function () { return self._udfAnchor(P, L.anchor2); });
            meta(L.fsd, 2, function () { return self._udfFileSet(P); });
        }
        meta(P.ptIso.lLba, P.ptIso.sectors, function () { return self._pathTable(P.ptIso, false, false); });
        meta(P.ptIso.mLba, P.ptIso.sectors, function () { return self._pathTable(P.ptIso, false, true); });
        if (J) {
            meta(P.ptJol.lLba, P.ptJol.sectors, function () { return self._pathTable(P.ptJol, true, false); });
            meta(P.ptJol.mLba, P.ptJol.sectors, function () { return self._pathTable(P.ptJol, true, true); });
        }
        P.dirs.forEach(function (d) {
            meta(d.isoLba, d.isoSectors, function () { return self._dirExtent(d, false); });
            if (J) meta(d.jolLba, d.jolSectors, function () { return self._dirExtent(d, true); });
            if (U) {
                var ps = L.partStart, subdirs = d.children.filter(function (c) { return c.isDir; }).length;
                meta(d.feLba, 1, function () {
                    return fileEntry(d.feLba - ps, true, d.fidBytes, [{ block: d.fidLba - ps, length: d.fidBytes }],
                        1 + subdirs, d.uid, self.created);
                });
                meta(d.fidLba, Math.max(1, sectorsFor(d.fidBytes)), function () { return self._udfDirStream(d, P); });
            }
        });
        if (U) {
            P.files.forEach(function (f) {
                meta(f.feLba, 1, function () {
                    var ext = [], left = f.size, at = f.lba - L.partStart;
                    while (left > 0) {
                        var n = Math.min(left, UDF_MAX_EXTENT);
                        ext.push({ block: at, length: n });
                        at += n / SECTOR; left -= n;
                    }
                    return fileEntry(f.feLba - L.partStart, false, f.size, ext, 1, f.uid, f.mtime || self.created);
                });
            });
        }
        if (boot) data(L.bootLba, boot);
        P.files.forEach(function (f) { if (f.size) data(f.lba, f.file); });   // an empty file's sector stays zero

        pieces.sort(function (a, b) { return a.lba - b.lba; });

        // Emit in LBA order. Metadata is staged and flushed into Blob chunks, so
        // the JS heap never holds all of it; content goes in by reference.
        var parts = [], stage = [], staged = 0, at = 0, ZERO = null;
        function flush() { if (stage.length) { parts.push(new Blob(stage)); stage = []; staged = 0; } }
        function zeros(n) {
            while (n > 0) {
                var k = Math.min(n, 256);
                if (!ZERO) ZERO = new Uint8Array(256 * SECTOR);
                stage.push(k === 256 ? ZERO : ZERO.subarray(0, k * SECTOR));
                staged += k * SECTOR; n -= k;
            }
        }
        pieces.forEach(function (p) {
            if (p.lba < at) throw new Error('iso-writer: layout overlap at sector ' + p.lba + ' (cursor ' + at + ')');
            zeros(p.lba - at);
            if (p.blob) {
                flush();
                parts.push(p.blob);
                var pad = p.n * SECTOR - p.blob.size;
                if (pad > 0) { stage.push(new Uint8Array(pad)); staged += pad; }
            } else {
                var bytes = p.make();
                if (bytes.length !== p.n * SECTOR) throw new Error('iso-writer: piece at ' + p.lba + ' is ' + bytes.length + ' bytes, planned ' + p.n * SECTOR);
                stage.push(bytes); staged += bytes.length;
                if (staged >= (4 << 20)) flush();
            }
            at = p.lba + p.n;
        });
        zeros(L.total - at);
        flush();

        var blob = new Blob(parts, { type: 'application/x-iso9660-image' });
        if (blob.size !== L.total * SECTOR) throw new Error('iso-writer: image is ' + blob.size + ' bytes, planned ' + L.total * SECTOR);
        var fs = ['iso9660'];
        if (J) fs.push('joliet');
        if (U) fs.push('udf');
        return {
            blob: blob,
            size: blob.size,
            totalSectors: L.total,
            fs: fs,
            renamed: P.renamed,
            layout: {
                pvdLba: L.pvd, svdLba: J ? L.svd : null, brvdLba: boot ? L.brvd : null,
                bootCatalogLba: boot ? L.catalog : null, bootImageLba: boot ? L.bootLba : null,
                pathTableLLba: P.ptIso.lLba, pathTableMLba: P.ptIso.mLba,
                pathTableLLbaJ: J ? P.ptJol.lLba : null, pathTableMLbaJ: J ? P.ptJol.mLba : null,
                udfPartitionStart: U ? L.partStart : null,
                totalSectors: L.total,
                dirs: P.dirs.length, files: P.files.length
            }
        };
    };

    w.__DiskImage.IsoBuilder = IsoBuilder;
    // Exposed for the unit tests: the naming rules are the contract readers see.
    w.__DiskImage.IsoNames = { joliet: jolietName, udf: udfName, isoIdent: isoIdent, fitName: fitName, JOLIET_MAX: JOLIET_MAX };
})(window);
